// Presentation Mode — shared state + DOM swap engine (embed side).
//
// For Branch / B2B / Head Office users, a navbar toggle (widgets/presentation-mode.js)
// flips the storefront into "presentation mode": the customer-facing RETAIL price
// is shown in place of the trade price, and the "Retail: R…" comparison line is
// hidden — so a branch user can walk a customer through the site showing retail
// pricing without logging out.
//
// Persists across pages (localStorage). Instant: no refetch and no widget
// re-render — vanilla price widgets tag their price node with BOTH values and
// this engine swaps the visible text. The React SPA handles its own surface
// natively via the same flag + event (see app/src/store/presentationStore).
//
// A price node opts in by carrying both attributes (its textContent is swapped):
//   data-pf-price-trade="R123.45"  data-pf-price-retail="R199.00"
// Retail comparison lines opt in with class `pf-retail-line`.
// Nodes with no retail value are left untouched — we NEVER blank a price.

var STORAGE_KEY = 'pf_presentation_mode';
export var PRESENTATION_EVENT = 'pf:presentation-mode';
var BODY_CLASS = 'pf-presentation-mode';

export function isPresentationMode() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch (e) {
    return false;
  }
}

function applyBodyClass(on) {
  try {
    var b = document.body;
    if (!b) return;
    if (on) b.classList.add(BODY_CLASS);
    else b.classList.remove(BODY_CLASS);
  } catch (e) { /* ignore */ }
}

/**
 * Swap tagged price nodes to their retail/trade variant and toggle retail
 * comparison lines. Idempotent and safe to run repeatedly.
 */
export function applyPresentation(root) {
  root = root || document;
  var on = isPresentationMode();
  var nodes;
  try {
    nodes = root.querySelectorAll('[data-pf-price-trade][data-pf-price-retail]');
  } catch (e) {
    return;
  }
  for (var i = 0; i < nodes.length; i++) {
    var el = nodes[i];
    var want = on ? el.getAttribute('data-pf-price-retail') : el.getAttribute('data-pf-price-trade');
    // Never blank out a price: if the wanted variant is missing, leave as-is.
    if (want == null || want === '') continue;
    if (el.textContent !== want) el.textContent = want;
  }
  var lines;
  try {
    lines = root.querySelectorAll('.pf-retail-line');
  } catch (e) {
    lines = [];
  }
  for (var j = 0; j < lines.length; j++) {
    lines[j].style.display = on ? 'none' : '';
  }
}

/** Flip the mode: persist, restyle, broadcast, and swap the current page now. */
export function setPresentationMode(on) {
  try {
    window.localStorage.setItem(STORAGE_KEY, on ? '1' : '0');
  } catch (e) { /* ignore */ }
  applyBodyClass(on);
  applyPresentation(document);
  try {
    window.dispatchEvent(new CustomEvent(PRESENTATION_EVENT, { detail: { on: !!on } }));
  } catch (e) {
    // Older browsers: fall back to a plain Event; listeners re-read the flag.
    try {
      var ev = document.createEvent('Event');
      ev.initEvent(PRESENTATION_EVENT, false, false);
      window.dispatchEvent(ev);
    } catch (e2) { /* ignore */ }
  }
}

var observer = null;
function ensureObserver() {
  if (observer || typeof MutationObserver === 'undefined') return;
  var scheduled = false;
  var schedule = function () {
    // Only do work while the mode is ON — when OFF, widgets already render trade
    // pricing natively, so there is nothing to swap and zero overhead.
    if (scheduled || !isPresentationMode()) return;
    scheduled = true;
    var run = function () { scheduled = false; applyPresentation(document); };
    if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(run);
    else window.setTimeout(run, 16);
  };
  observer = new MutationObserver(schedule);
  try {
    observer.observe(document.documentElement || document.body, { childList: true, subtree: true });
  } catch (e) { /* ignore */ }
}

/** Apply persisted state on load and keep late-rendered prices in sync. */
export function initPresentation() {
  applyBodyClass(isPresentationMode());
  applyPresentation(document);
  ensureObserver();
  // Cross-tab / cross-page consistency.
  try {
    window.addEventListener('storage', function (e) {
      if (e.key === STORAGE_KEY) {
        applyBodyClass(isPresentationMode());
        applyPresentation(document);
      }
    });
  } catch (e) { /* ignore */ }
}
